// Spec 45 component-behavior — cmp_ds_modal + aria_dialog.
//
// Modal dialog: blocks the underlying page until dismissed. Spec 45
// behavior:
//   * role='dialog' + aria-modal='true' + aria-labelledby (consumer-
//     supplied via labelledBy prop OR ModalHeader auto-id pattern)
//   * Focus trap inside the modal — Tab cycles through focusables;
//     Shift+Tab reverses; first focusable receives focus on open.
//   * Escape dismisses (the modal's own keydown handler — bubbling
//     not required since the overlay is a portal sibling of the page).
//   * Body scroll lock while open (overflow:hidden on document.body)
//     so background content doesn't scroll behind the modal.
//   * Click on the scrim closes the modal (configurable via
//     closeOnScrimClick=false for unsaved-changes guards).
//   * Restore focus to the trigger element on close.
//
// Composition slots: ModalHeader (auto-binds aria-labelledby) +
// ModalBody + ModalFooter — same shape as Card slots.

import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";

import { trapFocus } from "../lib/focus-trap.ts";
import { Portal } from "../lib/portal.tsx";
import { cx } from "../lib/cx.ts";

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, "role"> {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly children?: ReactNode;
  /** Optional id for the labelling element; consumer can also use ModalHeader which auto-binds. */
  readonly "aria-labelledby"?: string;
  /** Whether clicking the backdrop closes — defaults true. Set false for unsaved-changes guards. */
  readonly closeOnScrimClick?: boolean;
  /** Whether Escape closes — defaults true. */
  readonly closeOnEscape?: boolean;
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      open,
      onClose,
      "aria-labelledby": labelledBy,
      closeOnScrimClick = true,
      closeOnEscape = true,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const setRef = useCallback(
      (node: HTMLDivElement | null) => {
        dialogRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref !== null) ref.current = node;
      },
      [ref],
    );

    // Body scroll lock while open.
    useEffect(() => {
      if (!open) return;
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }, [open]);

    // Focus trap — install once the dialog node is mounted + open.
    useEffect(() => {
      if (!open) return;
      const node = dialogRef.current;
      if (node === null) return;
      return trapFocus(node);
    }, [open]);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (closeOnEscape && event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };

    const handleScrimClick = (event: MouseEvent<HTMLDivElement>) => {
      if (!closeOnScrimClick) return;
      // Only close when the click is on the scrim itself, not bubbling
      // up from the dialog content.
      if (event.target === event.currentTarget) {
        onClose();
      }
    };

    if (!open) return null;

    return (
      <Portal>
        {/* The scrim catches outside clicks for dismissal. Keyboard
            equivalent (Escape) lives on the dialog node below; users
            who can't use a pointer reach the dialog via the focus
            trap and Escape from there. The static-element rules
            don't model this two-element pattern, so disable below. */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          data-component="modal-scrim"
          className="nai-modal-scrim"
          onClick={handleScrimClick}
        >
          {/* role="dialog" makes this an interactive element per
              ARIA, but the lint rule's notion of "non-interactive"
              is HTML-tag-driven and doesn't account for role. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            ref={setRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy}
            tabIndex={-1}
            data-component="modal"
            className={cx("nai-modal", className)}
            onKeyDown={handleKeyDown}
            {...rest}
          >
            {children}
          </div>
        </div>
      </Portal>
    );
  },
);
Modal.displayName = "Modal";

export interface ModalHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Title text — used as the dialog's accessible name via aria-labelledby on the parent Modal. */
  readonly title: string;
  /** Optional id override; defaults to a useId() autogen. */
  readonly id?: string;
}

export const ModalHeader = forwardRef<HTMLDivElement, ModalHeaderProps>(
  ({ title, id, className, ...rest }, ref) => {
    const autoId = useId();
    const resolvedId = id ?? autoId;
    return (
      <div
        ref={ref}
        data-component="modal-header"
        className={cx("nai-modal-header", className)}
        {...rest}
      >
        <h2 id={resolvedId} data-component="modal-title" className="nai-modal-title">
          {title}
        </h2>
      </div>
    );
  },
);
ModalHeader.displayName = "ModalHeader";

export const ModalBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} data-component="modal-body" className={cx("nai-modal-body", className)} {...rest} />
  ),
);
ModalBody.displayName = "ModalBody";

export const ModalFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} data-component="modal-footer" className={cx("nai-modal-footer", className)} {...rest} />
  ),
);
ModalFooter.displayName = "ModalFooter";
